import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core'
import Chip from '@material-ui/core/Chip'
import { ChevronDown, Smile } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import EmojiOptions from '../EmojiOptions'
import { useStyles } from './styles'

const GREETING_TAGS = [
  {
    name: 'Nome',
    tag: '{{name}}',
    description: 'Nome completo do contato',
    text: 'João'
  },
  {
    name: 'Nome Completo',
    tag: '{{firstname}}',
    description: 'Primeiro nome do contato',
    text: 'João da Silva'
  },
  {
    name: 'Hora',
    tag: '{{time}}',
    description: 'Horário atual',
    text: '09:00'
  },
  {
    name: 'Saudação',
    tag: '{{greeting}}',
    description: 'Saudação conforme o horário do dia',
    text: 'Bom dia'
  },
  {
    name: 'Protocolo',
    tag: '{{protocol}}',
    description: 'Protocolo do Atendimento (YYYYMMDD-Atendimento)',
    text: '20230915'
  },
  {
    name: 'Email',
    tag: '{{email}}',
    description: 'Endereço de e-mail do contato',
    text: 'email@email.com'
  },
  {
    name: 'Departamento',
    tag: '{{queue}}',
    description: 'Departamento para o qual o atendimento foi transferido',
    text: 'Suporte'
  },
  {
    name: 'Ticket',
    tag: '{{ticket}}',
    description: 'Número do Atendimento',
    text: '102030'
  },
  {
    name: 'Usuário',
    tag: '{{user}}',
    description: 'Usuário que transferiu o atendimento',
    text: 'Pedro'
  }
]

const textToHtml = text => {
  if (!text) return ''

  const displayMap = GREETING_TAGS.reduce((acc, tag) => {
    acc[tag.tag] = tag.text
    return acc
  }, {})

  const tagsRegex = new RegExp(
    `(${GREETING_TAGS.map(t => t.tag.replace(/([{}])/g, '\\$1')).join('|')})`,
    'g'
  )

  const parts = text.split(tagsRegex)

  return parts
    .map(part => {
      if (displayMap[part]) {
        return `<span class="variavel-tag" contenteditable="false" data-original-text="${part}">
                            ${displayMap[part]} 
                        </span>`
      }
      return part.replace(/\n/g, '<br/>')
    })
    .join('')
}

// =================================================================
// FUNÇÕES DE GERENCIAMENTO DE CURSOR
// =================================================================

const saveSelection = editorEl => {
  const selection = window.getSelection()
  if (selection.rangeCount === 0) return null

  const range = selection.getRangeAt(0)
  if (!editorEl.contains(range.startContainer)) return null

  const preSelectionRange = range.cloneRange()
  preSelectionRange.selectNodeContents(editorEl)
  preSelectionRange.setEnd(range.startContainer, range.startOffset)

  const start = preSelectionRange.toString().length
  const end = start + range.toString().length

  return { start, end }
}

const restoreSelection = (editorEl, savedSelection) => {
  if (!savedSelection || !editorEl) return

  const range = document.createRange()

  let start = savedSelection.start
  let end = savedSelection.end
  let node = editorEl
  let foundStart = false
  let foundEnd = false

  const walker = document.createTreeWalker(
    editorEl,
    NodeFilter.SHOW_ALL,
    null,
    false
  )

  range.selectNodeContents(editorEl)
  range.collapse(true)

  while (node !== null && (!foundStart || !foundEnd)) {
    const isTextNode = node.nodeType === 3
    const nextLength = isTextNode ? node.length : 0

    if (isTextNode) {
      if (!foundStart && start <= nextLength) {
        range.setStart(node, start)
        foundStart = true
      }
      if (!foundEnd && end <= nextLength) {
        range.setEnd(node, end)
        foundEnd = true
      }

      start -= nextLength
      end -= nextLength
    } else if (
      node.nodeType === 1 &&
      node.getAttribute('contenteditable') === 'false'
    ) {
      if (!foundStart && start <= 0) {
        range.setStartBefore(node)
        foundStart = true
      }
      if (!foundEnd && end <= 0) {
        range.setEndBefore(node)
        foundEnd = true
      }
    }

    if (foundStart && foundEnd) break

    node = walker.nextNode()
  }

  if (!foundStart || !foundEnd) {
    range.setStart(editorEl, editorEl.childNodes.length)
    range.setEnd(editorEl, editorEl.childNodes.length)
  }

  const selection = window.getSelection()
  selection.removeAllRanges()
  selection.addRange(range)
}

// NOVO: Função para forçar o cursor ao final do elemento
const setCursorToEnd = editorEl => {
  if (!editorEl) return
  const range = document.createRange()
  const sel = window.getSelection()

  range.selectNodeContents(editorEl)
  range.collapse(false) // Move para o final

  sel.removeAllRanges()
  sel.addRange(range)
  editorEl.focus()
}

// =================================================================

const InputMessage = ({
  label = '',
  name,
  value,
  onChange,
  fieldsWithVariables = [],
  placeholder,
  onToggleVariables,
  disabled,
  isLarge = false,
  ...rest
}) => {
  const classes = useStyles()
  // const ref = useRef(null)
  const editorRef = useRef(null)
  const selectionRef = useRef(null)
  const isTypingRef = useRef(false)
  const inputId = `input-message-${name}`

  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const [showEmoji, setShowEmoji] = useState(false)

  const extractContent = useCallback(domElement => {
    if (!domElement) return ''

    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = domElement.innerHTML

    tempDiv.querySelectorAll('.variavel-tag').forEach(tag => {
      const originalText = tag.getAttribute('data-original-text') || ''
      const textNode = document.createTextNode(originalText)
      tag.replaceWith(textNode)
    })

    let outputHtml = tempDiv.innerHTML
    outputHtml = outputHtml.replace(/&nbsp;|\u00a0/gi, ' ')
    let plainText = outputHtml.replace(/<br\s*\/?>/gi, '\n')
    plainText = plainText.replace(/<\/?[^>]+(>|$)/g, '')

    plainText = plainText.trim()
    return plainText
  }, [])

  const handleInput = useCallback(() => {
    isTypingRef.current = true

    selectionRef.current = saveSelection(editorRef.current)

    const content = extractContent(editorRef.current)
    onChange({ target: { name, value: content } })
  }, [name, onChange, extractContent])

  const handleAddEmoji = useCallback(
    e => {
      const editor = editorRef.current
      if (!editor) return

      const emoji = e.native
      const emojiNode = document.createTextNode(emoji)

      if (selectionRef.current) {
        restoreSelection(editor, selectionRef.current)
      } else {
        editor.focus()
      }

      const selection = window.getSelection()
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0)

        if (editor.contains(range.commonAncestorContainer)) {
          range.deleteContents()
          range.insertNode(emojiNode)

          range.setStartAfter(emojiNode)
          range.collapse(true)

          selection.removeAllRanges()
          selection.addRange(range)
        } else {
          editor.appendChild(emojiNode)
          setCursorToEnd(editor)
        }
      } else {
        editor.appendChild(emojiNode)
        setCursorToEnd(editor)
      }

      selectionRef.current = saveSelection(editor)
      handleInput()
    },
    [handleInput]
  )

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Backspace') {
        const selection = window.getSelection()
        if (!selection.rangeCount || !selection.isCollapsed) return

        const range = selection.getRangeAt(0)
        const startNode = range.startContainer
        const startOffset = range.startOffset

        let nodeToRemove = null

        // Case when cursor is inside the editor element, e.g., between tags
        if (
          startNode.nodeType === Node.ELEMENT_NODE &&
          startNode === editorRef.current &&
          startOffset > 0
        ) {
          const nodeBefore = startNode.childNodes[startOffset - 1]
          if (
            nodeBefore &&
            nodeBefore.nodeType === Node.ELEMENT_NODE &&
            nodeBefore.classList.contains('variavel-tag')
          ) {
            nodeToRemove = nodeBefore
          }
        }
        // Case when cursor is at the beginning of a text node that follows a tag
        else if (startNode.nodeType === Node.TEXT_NODE && startOffset === 0) {
          const prevSibling = startNode.previousSibling
          if (
            prevSibling &&
            prevSibling.nodeType === Node.ELEMENT_NODE &&
            prevSibling.classList.contains('variavel-tag')
          ) {
            nodeToRemove = prevSibling
          }
        }

        if (nodeToRemove) {
          e.preventDefault()
          selectionRef.current = null // Prevent restoreSelection from running

          const parent = nodeToRemove.parentNode
          const offset = Array.prototype.indexOf.call(
            parent.childNodes,
            nodeToRemove
          )
          nodeToRemove.remove()

          // Manually place cursor where the node was
          const newRange = document.createRange()
          newRange.setStart(parent, offset)
          newRange.collapse(true)

          selection.removeAllRanges()
          selection.addRange(newRange)
          editorRef.current.focus() // Explicitly focus the editor

          // Update state, bypassing the save/restore selection logic
          isTypingRef.current = true
          const content = extractContent(editorRef.current)
          onChange({ target: { name, value: content } })

          setTimeout(() => {
            isTypingRef.current = false
          }, 0)
        }
      }
    },
    [extractContent, name, onChange]
  )

  useEffect(() => {
    if (editorRef.current) {
      const newHtml = textToHtml(value || '')
      const currentHtml = editorRef.current.innerHTML

      if (!isTypingRef.current && currentHtml !== newHtml) {
        editorRef.current.innerHTML = newHtml
      }

      if (selectionRef.current) {
        restoreSelection(editorRef.current, selectionRef.current)
        selectionRef.current = null

        setTimeout(() => {
          isTypingRef.current = false
        }, 0)
      }
    }
  }, [value])

  useEffect(() => {
    if (onToggleVariables) {
      onToggleVariables(isAccordionOpen)
    }
  }, [isAccordionOpen, onToggleVariables])


  const handleInsertVariable = useCallback(
    (tagText, visibleText) => {
      const editor = editorRef.current
      if (!editor) return

      if (!selectionRef.current) {
        setCursorToEnd(editor) // Se não houver seleção salva, move o cursor para o final
      } else {
        // Tenta restaurar a seleção salva anteriormente (se o foco não estiver no editor)
        restoreSelection(editor, selectionRef.current)
      }

      const variableTag = document.createElement('span')
      variableTag.className = 'variavel-tag'
      variableTag.setAttribute('contenteditable', 'false')
      variableTag.setAttribute('data-original-text', tagText)

      const textSpan = document.createElement('span')
      textSpan.textContent = visibleText
      variableTag.appendChild(textSpan)

      // const spaceNode = document.createTextNode(' ')

      // 3. Insere a tag na posição do cursor
      const selection = window.getSelection()
      if (
        selection.rangeCount > 0 &&
        editor.contains(selection.getRangeAt(0).commonAncestorContainer)
      ) {
        const range = selection.getRangeAt(0)
        range.deleteContents()

        range.insertNode(variableTag)
        // range.insertNode(spaceNode)

        range.setStartAfter(variableTag)
        range.setEndAfter(variableTag)
        selection.removeAllRanges()
        selection.addRange(range)
      } else {
        editor.appendChild(variableTag)
        // editor.appendChild(spaceNode)
        setCursorToEnd(editor)
      }

      // 4. Salva a seleção e dispara o update
      selectionRef.current = saveSelection(editorRef.current)
      handleInput()
    },
    [handleInput]
  )

  return (
    <Box className={classes.box}>
      <Typography
        variant="body1"
        component="label"
        className={classes.label}
        htmlFor={inputId}
      >
        {label}
      </Typography>

      <div className={classes.editorWrapper}>
        <div
          id={inputId}
          ref={editorRef}
          className={classes.editorContent}
          contentEditable="true"
          data-placeholder={placeholder}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onBlur={() =>
            (selectionRef.current = saveSelection(editorRef.current))
          }
          style={isLarge ? { height: '180px' } : {}}
          {...rest}
        ></div>
        <div className={classes.boxEmojiContainer}>
          <EmojiOptions
            disabled={disabled}
            handleAddEmoji={handleAddEmoji}
            showEmoji={showEmoji}
            setShowEmoji={setShowEmoji}
          >
            {/* O gatilho visual do componente */}
            <IconButton
              aria-label="emojiPicker"
              component="span"
              disabled={disabled}
              className={classes.button}
            >
              <Smile size={20} />
            </IconButton>
          </EmojiOptions>
        </div>
      </div>

      {/* Acordeão de Variáveis */}
      <Accordion
        elevation={0}
        className={classes.accordion}
        onChange={(event, expanded) => setIsAccordionOpen(expanded)}
        expanded={isAccordionOpen}
      >
        <AccordionSummary
          expandIcon={<ChevronDown />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          className={classes.accordionSummary}
        >
          <Typography className={classes.heading}>Variáveis</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <div className={classes.badgeVariableContainer}>
            {GREETING_TAGS.map(item => (
              <Tooltip key={item.tag} title={item.description} arrow>
                <Chip
                  label={item.name}
                  size="small"
                  clickable
                  onClick={() => handleInsertVariable(item.tag, item.name)}
                  className={classes.badgeVariable}
                />
              </Tooltip>
            ))}
          </div>
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default InputMessage
